// PostHog bootstrap. Key-less by design: when no key is configured every
// export is a safe no-op, so the app boots identically without analytics.
//
// Consent first (privacy decision 2026-10-31): PostHog is not even initialised
// until the visitor accepts analytics (config/consent.ts), so before that it
// sends nothing and stores nothing. "Refuse", or withdrawing later, opts it out
// and removes its cookies and storage. Session recording comes with the same
// consent. PostHog only ever gets the internal user id: no email or name.
import posthog, { type CaptureResult } from 'posthog-js';
import { captureLiveDemoSid, withoutLiveParam } from '../utils/liveDemoSid';
import { captureOAuthRequestParam, withoutOAuthRequestId } from '../utils/oauthRequestParam';
import { clearAnalyticsStorage, onConsentChange, readConsent, type ConsentChoice } from './consent';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com';

// The live demo's session id (`?live=<sid>`, the read key for a visitor's demo
// scans) leaves the address bar before PostHog records its first URL. So does
// a pending OAuth consent's id (`/connect?request=<id>`, #272).
captureLiveDemoSid();
captureOAuthRequestParam();

// An email address, also inside a URL (`%40`): the reset and forgot-password
// pages carry `?email=` in their address, which pageviews record.
const EMAIL = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi;

export function redactEmails(text: string): string {
  return text.replace(EMAIL, '[email]');
}

function scrub(value: unknown): unknown {
  if (typeof value === 'string') return redactEmails(value);
  if (Array.isArray(value)) return value.map(scrub);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, scrub(v)]));
  }
  return value;
}

/**
 * Last check on every event before it leaves: no email address in any
 * property (URLs, element text, person properties). Replays are masked while
 * they are recorded instead (maskTextFn below).
 */
export function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event === '$snapshot') return event;
  event.properties = scrub(event.properties) as CaptureResult['properties'];
  if (event.$set) event.$set = scrub(event.$set) as CaptureResult['$set'];
  if (event.$set_once) event.$set_once = scrub(event.$set_once) as CaptureResult['$set_once'];
  return event;
}

const CONFIG = {
  api_host: HOST,
  autocapture: true,
  // Route changes fire explicit $pageview from the router listener.
  capture_pageview: false,
  capture_pageleave: true,
  persistence: 'localStorage+cookie',
  // Links anonymous landing (basiqr.com) journeys with the app subdomain.
  // Analytics ids only: the session cookies are host-only (pentest AUTH-6).
  // The consent cookie is shared the same way, so one answer covers both.
  cross_subdomain_cookie: true,
  // An opt-out (refusal or withdrawal) also drops PostHog's stored ids.
  opt_out_persistence_by_default: true,
  before_send: scrubEvent,
  session_recording: {
    maskAllInputs: true,
    // Every text node goes through maskTextFn, which hides only email
    // addresses (a team list, a share's recipients, the account menu).
    maskTextSelector: '*',
    maskTextFn: (text: string) => redactEmails(text),
    // Replays record network URLs too: the product request carries the
    // live sid, the consent API calls carry the OAuth request id, and the
    // page's navigation entry keeps the URL it opened with.
    maskCapturedNetworkRequestFn: (request) => {
      request.name = redactEmails(withoutOAuthRequestId(withoutLiveParam(request.name)));
      return request;
    },
  },
  respect_dnt: true,
} satisfies Parameters<typeof posthog.init>[1];

interface Identity {
  id: string;
  role?: string;
  accountType?: string;
  isSubUser?: boolean;
}

let initialized = false; // posthog.init has run in this page
let capturing = false; // consent given and PostHog on
// The signed-in account, kept so it can be identified once consent arrives.
let identity: Identity | null = null;

/** Whether this build has analytics at all: no key, no banner. */
export const analyticsConfigured = () => !!KEY;
export const isAnalyticsEnabled = () => capturing;

function identifyNow(profile: Identity) {
  posthog.identify(profile.id, {
    role: profile.role,
    accountType: profile.accountType,
    isSubUser: profile.isSubUser,
  });
}

function startAnalytics(fromChoice: boolean) {
  if (!KEY || capturing) return;
  if (!initialized) {
    posthog.init(KEY, CONFIG);
    initialized = true;
  } else {
    // Accepted again after a refusal in the same page.
    posthog.opt_in_capturing({ captureEventName: false });
    posthog.startSessionRecording();
  }
  capturing = true;
  if (identity) identifyNow(identity);
  // The router listener already sent this page's view before the answer.
  if (fromChoice) posthog.capture('$pageview');
}

function stopAnalytics() {
  if (initialized && capturing) {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  }
  capturing = false;
  clearAnalyticsStorage();
}

function applyConsent(choice: ConsentChoice | null, fromChoice: boolean) {
  if (choice === 'granted') startAnalytics(fromChoice);
  else stopAnalytics();
}

let stopListening: (() => void) | undefined;
if (KEY) {
  // Nothing until the visitor has said yes; without a yes, also clear what
  // PostHog stored before consent was asked.
  applyConsent(readConsent(), false);
  stopListening = onConsentChange((choice) => applyConsent(choice, true));
}

/** Tests only: stop following consent changes (each fresh import listens). */
export function stopConsentListenerForTests() {
  stopListening?.();
}

/** No-op unless analytics were accepted; safe to call anywhere. */
export function track(event: string, props?: Record<string, unknown>) {
  if (!capturing) return;
  posthog.capture(event, props);
}

/**
 * Bind the browser identity to an account. distinct_id is the DynamoDB user
 * id; the person record gets the role and account type, never the email or
 * the name. Before consent the account is only remembered, and identified
 * if the visitor accepts.
 */
export function identifyUser(profile: Identity) {
  identity = { id: profile.id, role: profile.role, accountType: profile.accountType, isSubUser: profile.isSubUser };
  if (!capturing) return;
  identifyNow(identity);
}

export function resetIdentity() {
  identity = null;
  if (!capturing) return;
  posthog.reset();
}

export function capturePageview() {
  if (!capturing) return;
  posthog.capture('$pageview');
}

export default posthog;
